import React, { useEffect, useState, useMemo } from 'react';
import { Grid, Box, Snackbar, Typography, Chip, Tooltip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Alert } from '@mui/lab';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { EnhancedTable } from '../../general/EnhancedTable';
import SearchBox from '../../general/SearchBox';
import Modal from '../../general/modal';
import Form from '../../general/form';
import Loader from '../../Loader';
import { ButtonAdd, ButtonCancel, ButtonContinue, TypographyStyled } from '../../general/mini_components';
import { useSnackBarState } from '../../../hooks';
import { getRolesForInvestigationService, getPermissionsService, createCustomRoleService, updateCustomRoleService, deleteCustomRoleService } from '../../../services/roles';
import { roleErrorCodeToTranslationKey } from '../../../constants/roleErrorCodes';

interface IRoleWithPermissions {
    id: number;
    name: string;
    description: string;
    isDefault: boolean;
    permissions: string[];
    createdBy?: string;
}

interface RolesManagementProps extends LocalizeContextProps {
    uuidInvestigation: string;
}

const RolesManagement: React.FC<RolesManagementProps> = ({ uuidInvestigation, translate }) => {
    const [defaultRoles, setDefaultRoles] = useState<IRoleWithPermissions[]>([]);
    const [customRoles, setCustomRoles] = useState<IRoleWithPermissions[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [addingRole, setAddingRole] = useState(false);
    const [editingRole, setEditingRole] = useState<IRoleWithPermissions | null>(null);
    const [deletingRole, setDeletingRole] = useState<IRoleWithPermissions | null>(null);
    const [showSnackbar, setShowSnackbar] = useSnackBarState();
    const [searchText, setSearchText] = useState('');
    const [baseRoleId, setBaseRoleId] = useState<number | ''>('');
    const [baseRole, setBaseRole] = useState<IRoleWithPermissions | null>(null);
    const [permissionOptions, setPermissionOptions] = useState<Array<{ label: string; value: string }>>([]);

    // Permission list from GET /roles/permissions?uuidInvestigation=... (excludes EDIT_SUBMISSIONS for non-SHERWOOD_STAFF)
    const allPermissionOptions = useMemo(() => permissionOptions, [permissionOptions]);

    const CUSTOM_ROLE_CREATE_FORM: { [key: string]: any } = {
        name: {
            required: true,
            type: "text",
            name: "name",
            label: "investigation.share.custom_roles.form.name",
            shortLabel: "investigation.share.custom_roles.form.name",
            validation: "textMin2"
        },
        description: {
            required: false,
            type: "text",
            name: "description",
            label: "investigation.share.custom_roles.form.description",
            shortLabel: "investigation.share.custom_roles.form.description"
        },
        permissions: {
            required: false,
            type: "multioption",
            name: "permissions",
            label: "investigation.share.custom_roles.form.permissions",
            shortLabel: "investigation.share.custom_roles.form.permissions",
            options: allPermissionOptions
        }
    };

    const CUSTOM_ROLE_EDIT_FORM: { [key: string]: any } = {
        permissions: {
            required: false,
            type: "multioption",
            name: "permissions",
            label: "investigation.share.custom_roles.form.permissions",
            shortLabel: "investigation.share.custom_roles.form.permissions",
            options: allPermissionOptions
        }
    };

    useEffect(() => {
        fetchRoles();
    }, [uuidInvestigation]);

    async function fetchRoles() {
        setLoading(true);
        try {
            const [rolesResponse, permissionsResponse] = await Promise.all([
                getRolesForInvestigationService(uuidInvestigation),
                getPermissionsService(uuidInvestigation)
            ]);
            setDefaultRoles(rolesResponse.result.defaultRoles);
            setCustomRoles(rolesResponse.result.customRoles);
            setPermissionOptions(
                (permissionsResponse.result || []).map(p => ({
                    label: translate("investigation.share.permissions." + p.value),
                    value: p.value
                }))
            );
        } catch (error) {
            console.log("Error fetching data:", error);
        }
        setLoading(false);
    }

    function resetModal() {
        setShowModal(false);
        setAddingRole(false);
        setEditingRole(null);
        setDeletingRole(null);
        setBaseRoleId('');
        setBaseRole(null);
    }

    async function handleCreateRole(values: any) {
        const permissions = (values.permissions || []).map((p: { multioption: string }) => p.multioption);
        try {
            const response = await createCustomRoleService(uuidInvestigation, {
                name: values.name,
                description: values.description || '',
                permissions,
                baseRoleId: baseRoleId !== '' ? (baseRoleId as number) : undefined
            });
            setCustomRoles(prev => [...prev, response.result]);
            setShowSnackbar({ show: true, message: "investigation.share.custom_roles.success_create", severity: "success" });
        } catch (error: any) {
            const errorCode = error?.response?.data?.errorCode;
            const message = errorCode && errorCode in roleErrorCodeToTranslationKey
                ? roleErrorCodeToTranslationKey[errorCode as keyof typeof roleErrorCodeToTranslationKey]
                : "investigation.share.error.description";
            setShowSnackbar({ show: true, message, severity: "error" });
        }
        resetModal();
    }

    async function handleUpdateRole(values: any) {
        if (!editingRole) return;
        const permissions = (values.permissions || []).map((p: { multioption: string }) => p.multioption);
        try {
            const response = await updateCustomRoleService(uuidInvestigation, editingRole.id, { permissions });
            setCustomRoles(prev => prev.map(r => r.id === editingRole.id ? response.result : r));
            setShowSnackbar({ show: true, message: "investigation.share.custom_roles.success_update", severity: "success" });
        } catch (error: any) {
            const errorCode = error?.response?.data?.errorCode;
            const message = errorCode && errorCode in roleErrorCodeToTranslationKey
                ? roleErrorCodeToTranslationKey[errorCode as keyof typeof roleErrorCodeToTranslationKey]
                : "investigation.share.error.description";
            setShowSnackbar({ show: true, message, severity: "error" });
        }
        resetModal();
    }

    async function handleDeleteRole() {
        if (!deletingRole) return;
        try {
            await deleteCustomRoleService(uuidInvestigation, deletingRole.id);
            setCustomRoles(prev => prev.filter(r => r.id !== deletingRole.id));
            setShowSnackbar({ show: true, message: "investigation.share.custom_roles.success_delete", severity: "success" });
        } catch (error: any) {
            const errorCode = error?.response?.data?.errorCode;
            const message = errorCode && errorCode in roleErrorCodeToTranslationKey
                ? roleErrorCodeToTranslationKey[errorCode as keyof typeof roleErrorCodeToTranslationKey]
                : "investigation.share.error.description";
            setShowSnackbar({ show: true, message, severity: "error" });
        }
        resetModal();
    }

    function openAddModal() {
        setBaseRoleId('');
        setBaseRole(null);
        setAddingRole(true);
        setShowModal(true);
    }

    function openEditModal(roleId: number) {
        const role = customRoles.find(r => r.id === roleId);
        if (role) {
            setEditingRole(role);
            setShowModal(true);
        }
    }

    function openDeleteModal(roleId: number) {
        const role = customRoles.find(r => r.id === roleId);
        if (role) {
            setDeletingRole(role);
            setShowModal(true);
        }
    }

    function renderPermissionsCell(permissions: string[]) {
        const MAX_VISIBLE = 3;
        const visible = permissions.slice(0, MAX_VISIBLE);
        const remaining = permissions.length - MAX_VISIBLE;

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {visible.map(p => (
                    <Chip key={p} size="small" label={translate("investigation.share.permissions." + p)} />
                ))}
                {remaining > 0 && (
                    <Tooltip title={permissions.slice(MAX_VISIBLE).map(p => translate("investigation.share.permissions." + p)).join(", ")} arrow>
                        <Chip size="small" label={`+${remaining}`} variant="outlined" />
                    </Tooltip>
                )}
            </Box>
        );
    }

    function getEditInitialData() {
        if (!editingRole) return undefined;
        return {
            permissions: editingRole.permissions.map(p => ({ multioption: p }))
        };
    }

    function getCreateInitialData() {
        return {
            name: '',
            description: baseRole?.description ?? '',
            permissions: (baseRole?.permissions ?? []).map(p => ({ multioption: p }))
        };
    }

    function renderModalContent() {
        if (addingRole) {
            return (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="start-from-label">
                                <Translate id="investigation.share.custom_roles.form.start_from" />
                            </InputLabel>
                            <Select
                                labelId="start-from-label"
                                value={baseRoleId}
                                label={translate("investigation.share.custom_roles.form.start_from")}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '') {
                                        setBaseRoleId('');
                                        setBaseRole(null);
                                    } else {
                                        const id = Number(val);
                                        const r = defaultRoles.find(ro => ro.id === id) ?? null;
                                        setBaseRoleId(id);
                                        setBaseRole(r);
                                    }
                                }}
                            >
                                <MenuItem value="">—</MenuItem>
                                {defaultRoles.map(r => (
                                    <MenuItem key={r.id} value={r.id}>
                                        {translate("investigation.share.roles." + r.name) !== "Missing translationId: investigation.share.roles." + r.name
                                            ? translate("investigation.share.roles." + r.name)
                                            : r.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Form
                            key={`create-role-${baseRoleId || 'empty'}`}
                            fields={CUSTOM_ROLE_CREATE_FORM}
                            fullWidth
                            initialData={getCreateInitialData()}
                            callBackForm={handleCreateRole}
                            closeCallBack={resetModal}
                        />
                    </Grid>
                </Grid>
            );
        }
        if (editingRole) {
            return (
                <Form
                    fields={CUSTOM_ROLE_EDIT_FORM}
                    fullWidth
                    callBackForm={handleUpdateRole}
                    initialData={getEditInitialData()}
                    closeCallBack={resetModal}
                />
            );
        }
        if (deletingRole) {
            return (
                <Grid container>
                    <Grid item xs={12}>
                        <Typography variant="body2" gutterBottom>
                            <Translate id="investigation.share.custom_roles.delete_confirmation" />
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 'bold' }} gutterBottom>
                            {deletingRole.name}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} style={{ paddingTop: '1rem' }}>
                        <ButtonCancel onClick={resetModal} spaceright={1}>
                            <Translate id="general.cancel" />
                        </ButtonCancel>
                        <ButtonContinue onClick={handleDeleteRole} color="green">
                            <Translate id="general.delete" />
                        </ButtonContinue>
                    </Grid>
                </Grid>
            );
        }
        return null;
    }

    function getModalTitle() {
        if (addingRole) return translate("investigation.share.custom_roles.add_role");
        if (editingRole) return translate("investigation.share.custom_roles.edit_role");
        if (deletingRole) return translate("investigation.share.custom_roles.delete_role");
        return "";
    }

    const matchesSearch = (role: IRoleWithPermissions, term: string) => {
        if (!term.trim()) return true;
        const q = term.toLowerCase().trim();
        return (
            role.name.toLowerCase().includes(q) ||
            (role.description || '').toLowerCase().includes(q)
        );
    };

    const filteredDefaultRoles = useMemo(
        () => defaultRoles.filter(role => matchesSearch(role, searchText)),
        [defaultRoles, searchText]
    );
    const filteredCustomRoles = useMemo(
        () => customRoles.filter(role => matchesSearch(role, searchText)),
        [customRoles, searchText]
    );

    if (loading) {
        return <Loader />;
    }

    const defaultRolesHeadCells = [
        { id: "name", alignment: "left", label: <Translate id="investigation.share.custom_roles.table.name" />, width: "18%" },
        { id: "description", alignment: "left", label: <Translate id="investigation.share.custom_roles.table.description" />, width: "45%" },
        { id: "permissions", alignment: "left", label: <Translate id="investigation.share.custom_roles.table.permissions" /> }
    ];

    const customRolesHeadCells = [
        { id: "name", alignment: "left", label: <Translate id="investigation.share.custom_roles.table.name" />, width: "18%" },
        { id: "description", alignment: "left", label: <Translate id="investigation.share.custom_roles.table.description" />, width: "45%" },
        { id: "permissions", alignment: "left", label: <Translate id="investigation.share.custom_roles.table.permissions" /> }
    ];

    const defaultRolesRows = filteredDefaultRoles.map(role => ({
        id: role.id,
        name: translate("investigation.share.roles." + role.name) !== "Missing translationId: investigation.share.roles." + role.name
            ? translate("investigation.share.roles." + role.name)
            : role.name,
        description: role.description,
        permissions: renderPermissionsCell(role.permissions)
    }));

    const customRolesRows = filteredCustomRoles.map((role: IRoleWithPermissions) => ({
        id: role.id,
        name: role.name,
        description: role.description,
        permissions: renderPermissionsCell(role.permissions)
    }));

    return (
        <>
            <Modal key="modal" open={showModal} closeModal={resetModal}
                title={getModalTitle()}>
                {renderModalContent()}
            </Modal>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={showSnackbar.show}
                autoHideDuration={2000}
                onClose={() => setShowSnackbar({ show: false })}>
                <div>
                    {showSnackbar.message && showSnackbar.severity &&
                        <Alert onClose={() => setShowSnackbar({ show: false })} severity={showSnackbar.severity}>
                            <Translate id={showSnackbar.message} />
                        </Alert>
                    }
                </div>
            </Snackbar>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <SearchBox
                        hideSearchPatient={false}
                        textField={{
                            label: 'investigation.share.search_roles',
                            helperText: 'investigation.share.search_roles_helper',
                            callBack: setSearchText
                        }}
                        filterItems={[]}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TypographyStyled gutterBottom variant="h6" component="h3">
                        <Translate id="investigation.share.custom_roles.default_roles" />
                    </TypographyStyled>
                    <Box sx={{ '& table': { tableLayout: 'fixed', width: '100%' } }}>
                        <EnhancedTable
                            noHeader
                            noSelectable
                            headCells={defaultRolesHeadCells}
                            rows={defaultRolesRows}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <TypographyStyled gutterBottom variant="h6" component="h3">
                        <Translate id="investigation.share.custom_roles.title" />
                        <ButtonAdd
                            type="button"
                            data-testid="add_custom_role"
                            onClick={openAddModal}
                            style={{ marginLeft: '0.5rem' }}
                        />
                    </TypographyStyled>
                    {customRoles.length === 0 ? (
                        <Box mt={2}>
                            <TypographyStyled variant="body2" component="div" gutterBottom>
                                <Translate id="investigation.share.custom_roles.no_custom_roles" />
                            </TypographyStyled>
                        </Box>
                    ) : (
                        <Box sx={{ '& table': { tableLayout: 'fixed', width: '100%' } }}>
                            <EnhancedTable
                                noSelectable
                                noHeader
                                headCells={customRolesHeadCells}
                                rows={customRolesRows}
                                actions={[
                                    { type: "edit", func: (id: number) => openEditModal(id) },
                                    { type: "delete", func: (id: number) => openDeleteModal(id) }
                                ]}
                            />
                        </Box>
                    )}
                </Grid>
            </Grid>
        </>
    );
};

export default withLocalize(RolesManagement);
